import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface StorageItem {
  asin: string;
  sku: string | null;
  marketplace: string;
  productName: string;
  sizeTier: string;
  monthlyFee: number;
  avgQtyOnHand: number;
  avgDailyVelocity: number;
  daysOfSupply: number | null;
  recommendation: string;
}

interface StorageData {
  month: string | null;
  marketplaces: string[];
  totalMonthlyFees: number;
  previousMonthFees: number | null;
  potentialMonthlySavings: number;
  removalCandidates: number;
  items: StorageItem[];
}

interface Account {
  id: string;
  name: string;
  is_active?: number;
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

// ── Component ────────────────────────────────────────────────────────────────

export function InventoryStorage() {
  const [data, setData] = useState<StorageData | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [marketplace, setMarketplace] = useState('');
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { displayName, parentNickname } = useNicknames();

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      const active = accts.filter(a => a.is_active !== 0);
      setAccounts(active);
      if (active.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (accounts.length === 0) return;
    setLoading(true);
    setError('');

    const mpParam = marketplace ? `?marketplace=${marketplace}` : '';
    Promise.all(
      accounts.map(a => api.get<StorageData>(`/inventory/storage/${a.id}${mpParam}`).catch(() => null))
    ).then(results => {
      const valid = results.filter((r): r is StorageData => r !== null && r.month !== null);
      if (valid.length === 0) { setData(null); return; }
      // Merge all accounts
      const merged: StorageData = {
        month: valid[0].month,
        marketplaces: [...new Set(valid.flatMap(v => v.marketplaces))],
        totalMonthlyFees: valid.reduce((s, v) => s + v.totalMonthlyFees, 0),
        previousMonthFees: valid.some(v => v.previousMonthFees !== null) ? valid.reduce((s, v) => s + (v.previousMonthFees ?? 0), 0) : null,
        potentialMonthlySavings: valid.reduce((s, v) => s + v.potentialMonthlySavings, 0),
        removalCandidates: valid.reduce((s, v) => s + v.removalCandidates, 0),
        items: valid.flatMap(v => v.items),
      };
      merged.items.sort((a, b) => b.monthlyFee - a.monthlyFee);
      if (!marketplace) setAvailableMarkets(merged.marketplaces);
      setData(merged);
    }).catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [accounts, marketplace]);

  if (loading) return <LoadingSkeleton />;

  const trendPct = data?.previousMonthFees && data.previousMonthFees > 0
    ? ((data.totalMonthlyFees - data.previousMonthFees) / data.previousMonthFees * 100)
    : null;

  const actionItems = data?.items.filter(i => i.recommendation !== 'OK') ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Storage Optimization</h2>
          <p className="text-sm text-gray-500">
            {data?.month ? `Storage fees for ${data.month}` : 'Optimize storage costs and identify removal candidates.'}
          </p>
        </div>
      </div>

      {/* Marketplace filter */}
      {availableMarkets.length > 1 && (
        <div className="flex gap-1.5">
          <button onClick={() => setMarketplace('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => setMarketplace(id === marketplace ? '' : id)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!data?.month && !error && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm font-medium text-gray-900">No storage fee data yet</p>
          <p className="mt-1 text-xs text-gray-500">
            Storage fees are imported from Amazon reports during sync. Run a storage fees sync to populate this page.
          </p>
        </div>
      )}

      {data?.month && (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Monthly Storage Cost</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{fmtCurrency(data.totalMonthlyFees)}</p>
              {trendPct !== null && (
                <p className={`mt-1 text-xs font-medium ${trendPct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trendPct > 0 ? '+' : ''}{trendPct.toFixed(1)}% vs prior month
                </p>
              )}
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Potential Savings</p>
              <p className="mt-1 text-lg font-semibold text-green-600">{fmtCurrency(data.potentialMonthlySavings)}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Removal Candidates</p>
              <p className={`mt-1 text-lg font-semibold ${data.removalCandidates > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {data.removalCandidates}
              </p>
            </div>
          </div>

          {/* Action Items Table */}
          {actionItems.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h3 className="text-sm font-semibold">Action Items</h3>
              <p className="mt-1 text-xs text-gray-500">Items that may benefit from removal, reduction, or review.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Product</th>
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Size Tier</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Monthly Fee</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Vel/day</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Days of Supply</th>
                      <th className="pb-2 text-center text-xs font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {actionItems.map((item) => (
                      <tr key={item.asin}>
                        <td className="py-2">
                          {item.sku && parentNickname(item.sku) && <div className="text-[10px] text-gray-400">{parentNickname(item.sku)}</div>}
                          <div className="font-medium text-gray-900">{item.sku ? displayName(item.sku) : item.asin}</div>
                        </td>
                        <td className="py-2 text-gray-500">{item.sizeTier}</td>
                        <td className="py-2 text-right font-medium text-gray-900">{fmtCurrency(item.monthlyFee)}</td>
                        <td className="py-2 text-right text-gray-700">{item.avgDailyVelocity}</td>
                        <td className="py-2 text-right text-gray-700">{item.daysOfSupply ?? '—'}</td>
                        <td className="py-2 text-center"><RecommendationBadge rec={item.recommendation} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Full Table */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold">All Storage Fees</h3>
            {data.items.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">No storage fee data for this month.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Product</th>
                      <th className="pb-2 text-left text-xs font-medium text-gray-500">Size Tier</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Monthly Fee</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Avg Qty</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Vel/day</th>
                      <th className="pb-2 text-right text-xs font-medium text-gray-500">Days Supply</th>
                      <th className="pb-2 text-center text-xs font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.items.map((item) => (
                      <tr key={item.asin}>
                        <td className="py-2">
                          {item.sku && parentNickname(item.sku) && <div className="text-[10px] text-gray-400">{parentNickname(item.sku)}</div>}
                          <div className="font-medium text-gray-900">{item.sku ? displayName(item.sku) : item.asin}</div>
                        </td>
                        <td className="py-2 text-gray-500">{item.sizeTier}</td>
                        <td className="py-2 text-right font-medium text-gray-900">{fmtCurrency(item.monthlyFee)}</td>
                        <td className="py-2 text-right text-gray-500">{item.avgQtyOnHand}</td>
                        <td className="py-2 text-right text-gray-700">{item.avgDailyVelocity}</td>
                        <td className="py-2 text-right text-gray-700">{item.daysOfSupply ?? '—'}</td>
                        <td className="py-2 text-center"><RecommendationBadge rec={item.recommendation} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function RecommendationBadge({ rec }: { rec: string }) {
  const styles: Record<string, string> = {
    REMOVE: 'bg-red-100 text-red-700',
    REDUCE: 'bg-orange-100 text-orange-700',
    REVIEW: 'bg-yellow-100 text-yellow-700',
    OK: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[rec] ?? 'bg-gray-100 text-gray-600'}`}>
      {rec}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-40 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-48 rounded-xl bg-gray-200" />
    </div>
  );
}

function fmtCurrency(val: number): string {
  return '$' + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
